import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';

/**
 * Last-resort screen shown when a render error escapes every other boundary.
 *
 * Deliberately dependency-free: it sits OUTSIDE <App />, so it has no access to the
 * Apollo, Redux or I18nextProvider contexts — and if one of those is what crashed,
 * consuming it here would take the fallback down too. i18next is read from the
 * singleton rather than the React context for the same reason, with literal defaults
 * so the screen still reads correctly if i18n itself failed to initialise.
 *
 * Recovery is a full page reload, not resetError(): the boundary does not re-mount on
 * navigation, so calling resetError() while the underlying DOM is still corrupted
 * (which is exactly what browser translation does) would just crash again and trap the
 * user on this screen.
 */
const AppCrashFallback = ({ error }) => {
  const t = (key, defaultValue) => i18next.t(`error:${key}`, defaultValue);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      role="alert"
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('appCrash.title', 'Algo salió mal')}
        </h1>
        <p className="max-w-md text-base text-muted-foreground">
          {t(
            'appCrash.description',
            'No pudimos mostrar esta página. Vuelve a cargarla para continuar.'
          )}
        </p>
      </div>

      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
      >
        <span>{t('appCrash.reload', 'Recargar la página')}</span>
      </button>

      {process.env.NODE_ENV === 'development' && error && (
        <pre className="max-w-2xl overflow-x-auto rounded-md bg-muted p-4 text-left text-xs text-muted-foreground">
          {error.toString()}
        </pre>
      )}
    </div>
  );
};

AppCrashFallback.propTypes = {
  error: PropTypes.object,
};

export default AppCrashFallback;
